import React, { useEffect, useState } from "react";
import { Receipt, CheckCircle2, X, Trash2 } from "lucide-react";
import { adminListInvoices, adminUpdateInvoice, adminDeleteInvoice } from "../../lib/api";

export default function InvoicesPanel({ onToast }) {
  const [list, setList] = useState([]);

  const load = async () => setList(await adminListInvoices());
  useEffect(() => { load(); }, []);

  const markPaid = async (id) => {
    await adminUpdateInvoice(id, { status: "paid" });
    onToast?.("Marked paid");
    await load();
  };
  const cancel = async (id) => {
    await adminUpdateInvoice(id, { status: "cancelled" });
    onToast?.("Cancelled");
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete invoice?")) return;
    await adminDeleteInvoice(id);
    onToast?.("Deleted");
    await load();
  };

  const badge = (s) => {
    const map = { paid: "bg-green-100 text-green-700", unpaid: "bg-yellow-100 text-yellow-700", cancelled: "bg-gray-100 text-gray-600", refunded: "bg-red-100 text-red-700" };
    return `text-xs font-bold px-2 py-1 rounded ${map[s] || "bg-gray-100 text-gray-700"}`;
  };

  return (
    <div>
      <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Invoices</h2>
      <p className="text-[#11151C]/60 text-sm mt-1">All invoices from renewals, manual orders, and checkouts.</p>

      <div className="mt-6 border border-[#11151C]/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFBF6]">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Invoice #</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Items</th>
              <th className="text-left p-3">Subtotal</th>
              <th className="text-left p-3">GST</th>
              <th className="text-left p-3">Total</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-[#11151C]/50">No invoices yet.</td></tr>
            )}
            {list.map((i) => {
              const gst = i.gst_breakdown || {};
              const subtotal = i.subtotal_inr ?? gst.subtotal ?? i.total_inr ?? 0;
              const gstTotal = gst.gst_total || 0;
              return (
                <tr key={i.id} className="border-t border-[#11151C]/5">
                  <td className="p-3 text-xs">{new Date(i.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs font-mono">{i.invoice_no || i.id.slice(0, 8)}</td>
                  <td className="p-3 text-xs uppercase font-bold text-[#11151C]/70">{i.type}</td>
                  <td className="p-3 text-xs">
                    {(i.items || []).map((it, idx) => (
                      <div key={idx}>{it.description} · ₹{it.amount}×{it.qty || 1}</div>
                    ))}
                  </td>
                  <td className="p-3 text-sm">₹{(subtotal || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-xs">
                    {gstTotal ? (
                      <span title={`IGST: ₹${gst.igst || 0} · CGST: ₹${gst.cgst || 0} · SGST: ₹${gst.sgst || 0}`}>
                        ₹{gstTotal.toLocaleString("en-IN")}
                      </span>
                    ) : <span className="text-[#11151C]/40">—</span>}
                  </td>
                  <td className="p-3 font-bold">₹{(i.total_inr || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3"><span className={badge(i.status)}>{i.status}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {i.status === "unpaid" && (
                      <button onClick={() => markPaid(i.id)} title="Mark paid" className="p-1.5 hover:bg-green-50 rounded-lg"><CheckCircle2 className="w-4 h-4 text-green-600" /></button>
                    )}
                    {i.status !== "cancelled" && i.status !== "paid" && (
                      <button onClick={() => cancel(i.id)} title="Cancel" className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-600" /></button>
                    )}
                    <button onClick={() => remove(i.id)} title="Delete" className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
